import type { ReactNode } from 'react';

type AlertTone = 'error' | 'success' | 'info';

interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  onClose?: () => void;
}

const styles: Record<AlertTone, { wrap: string; icon: string; name: string }> = {
  error: {
    wrap: 'bg-accent-50 border-accent-200 text-accent-900',
    icon: 'text-accent-600',
    name: 'ri-error-warning-line',
  },
  success: {
    wrap: 'bg-primary-50 border-primary-200 text-primary-900',
    icon: 'text-primary-600',
    name: 'ri-checkbox-circle-line',
  },
  info: {
    wrap: 'bg-secondary-50 border-secondary-200 text-secondary-900',
    icon: 'text-secondary-500',
    name: 'ri-information-line',
  },
};

export default function Alert({ tone = 'info', title, children, onClose }: AlertProps) {
  const style = styles[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[13px] leading-relaxed ${style.wrap}`}
    >
      <span className={`flex items-center justify-center w-4 h-4 mt-0.5 shrink-0 ${style.icon}`}>
        <i className={`${style.name} text-base`} aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold font-label mb-0.5">{title}</p> : null}
        {children ? <div className="opacity-90">{children}</div> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          className="flex items-center justify-center w-4 h-4 shrink-0 opacity-60 hover:opacity-100 cursor-pointer"
        >
          <i className="ri-close-line text-base" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}